import React from "react";
import { Link } from "react-router-dom";
import { apiService } from "../../../../../api/api";
import { mvMetadataGapApi } from "../../../../../api/mvMetadataGapApi";
import { ROUTES } from "../../../../../config/routes";
import { MusicAuditStats } from "../../../../../utils/@types";
import { WidgetComponentProps } from "../../types";
import { useWidgetData } from "../../useWidgetData";
import {
  CardError,
  CardLoading,
  Freshness,
  IssueBars,
  Kpi,
  KpiGrid,
  countTone,
} from "../../ui";
import { latestOf } from "./cardHelpers";

/** Audit types shown on MusicAssetAuditsDashboard, with the route for each audit. */
const AUDITS: Array<{ auditType: string; label: string; to: string }> = [
  { auditType: "incorrectAssetTypes", label: "Incorrect asset types", to: ROUTES.ASSET_TYPE_AUDIT },
  {
    auditType: "mismatchedSoundRecordings",
    label: "Mismatched sound recordings",
    to: ROUTES.MISMATCHED_SOUND_RECORDINGS,
  },
  { auditType: "stubAssets", label: "Stub assets", to: ROUTES.STUB_ASSET_AUDIT },
  {
    auditType: "missingSoundRecordings",
    label: "Missing sound recordings",
    to: ROUTES.MUSIC_VIDEO_EMBEDDING,
  },
];

/** Same unfiltered params the dashboard sends (see buildStatsParams there). */
const statsParams = (auditType: string) => ({
  auditType,
  legalEntityType: "ALL",
  channelId: "ALL",
  channelManagerName: "ALL",
});

interface MusicAssetsData {
  stats: Array<MusicAuditStats | null>;
  mvPendingReviews: number | null;
}

const MusicAssetsCard: React.FC<WidgetComponentProps> = () => {
  const { data, loading, error, reload } = useWidgetData<MusicAssetsData>("music-assets", async () => {
    const [stats, mvStats] = await Promise.all([
      Promise.all(
        AUDITS.map((a) =>
          apiService.musicAssetAudit.getMusicAuditStats(statsParams(a.auditType)).catch(() => null)
        )
      ),
      mvMetadataGapApi.getStats().catch(() => null),
    ]);
    if (stats.every((s) => s === null) && mvStats === null) {
      throw new Error("Could not load music asset audit stats");
    }
    return { stats, mvPendingReviews: mvStats?.pendingReviews ?? null };
  });

  if (loading && !data) return <CardLoading />;
  if (error) return <CardError message={error} onRetry={reload} />;
  if (!data) return null;

  const totalIssues = data.stats.reduce((sum, s) => sum + (Number(s?.totalIssues) || 0), 0);
  const newIssues = data.stats.reduce((sum, s) => sum + (Number(s?.newIssues) || 0), 0);
  const bars = AUDITS.map((a, i) => ({
    label: a.label,
    count: Number(data.stats[i]?.totalIssues) || 0,
    to: a.to,
  }));

  return (
    <>
      <KpiGrid columns={3}>
        <Kpi
          label="Open audit issues"
          value={totalIssues}
          tone={countTone(totalIssues)}
          to={ROUTES.YOUTUBE_MUSIC_ASSET_AUDITS_DASHBOARD}
          emphasis
        />
        <Kpi
          label="New issues"
          value={newIssues}
          tone={newIssues > 0 ? "info" : "neutral"}
          to={ROUTES.YOUTUBE_MUSIC_ASSET_AUDITS_DASHBOARD}
        />
        <Kpi
          label="MV metadata gaps"
          value={data.mvPendingReviews}
          tone={countTone(data.mvPendingReviews)}
          hint="Pending review"
          to={ROUTES.MV_METADATA_GAPS}
        />
      </KpiGrid>

      <IssueBars title="Issues by audit" items={bars} emptyText="No audit data yet" />

      <div className="home-card__meta">
        <Link className="home-link" to={ROUTES.YOUTUBE_MUSIC_ASSET_AUDITS_DASHBOARD}>
          Audit dashboard
        </Link>
        <Freshness at={latestOf(data.stats.map((s) => s?.updatedAt))} />
      </div>
    </>
  );
};

export default MusicAssetsCard;
